import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { BreakdownsContext } from './contexts/BreakdownsContext';
import { PieChart, PieChartDatum } from '../../../components/PieChart/PieChart';
import { BreakdownsContextType } from './contexts/BreakdownsContextTypes';
import { BreakdownsContainer } from '../../../components/BreakdownsContainer/BreakdownsContainer';
import { formatWithCustomGigaBillion } from '../../../utils/fomatNumbers';
import { mapBotEntityTypes } from '../../../utils/actors';

interface BotsChartProps {
  narrative: any;
  mode?: 'actors' | 'publications';
  isClickable?: boolean;
  isShare?: boolean;
}

type ChartData = ChartDataItem[];

interface ChartDataItem {
  inauthentic_type: string;
  actors_count: number;
  actors_share: number;
  publications_count: number;
  publications_share: number;
}

export const BotsChart: React.FC<BotsChartProps> = ({
  mode = 'actors',
  isClickable = true,
}) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [actorsData, setActorsData] = useState<PieChartDatum[] | null>(null);
  const [publicationsData, setPublicationsData] = useState<PieChartDatum[] | null>(null);
  const [currentMode, setCurrentMode] = useState<'actors' | 'publications'>(
    mode,
  );
  const {
    inauthenticBehavior,
    openModal,
    setTypesFilter,
    setSubtypesFilter,
    setCurrentWidgetName,
    setBotsExcludeFilter,
    setModalTitle,
  } = useContext(BreakdownsContext) as BreakdownsContextType;
  const botEntityTypes = mapBotEntityTypes(t);

  const mapEntitiesToColors = {
    immature_account: '#FF9736',
    suspended_by_platform: '#FF3636',
    inauthentic_behavior: '#A267DC',
  };

  const getSliceColor = (item: ChartDataItem) =>
    item.inauthentic_type === null
      ? '#306DCF'
      : mapEntitiesToColors[item.inauthentic_type?.toLowerCase() as keyof typeof mapEntitiesToColors];

  const getChartDataByMode = (mode: 'actors' | 'publications'): PieChartDatum[] => {
    if (!chartData) {
      return [];
    }

    return chartData.map((item) => {
      const typeName = item?.inauthentic_type
        ? botEntityTypes[
            item?.inauthentic_type?.toLowerCase() as keyof typeof botEntityTypes
          ]?.title
        : t('Other');
      const countMode =
        mode === 'actors' ? item.actors_count : item.publications_count;

      return {
        name: typeName,
        value: countMode,
        formattedCount: formatWithCustomGigaBillion(countMode),
        color: getSliceColor(item),
      };
    });
  };

  useEffect(() => {
    if (!inauthenticBehavior) {
      return;
    }

    setChartData(inauthenticBehavior?.inauthentic_distribution || null);
  }, [inauthenticBehavior]);

  useEffect(() => {
    if (chartData) {
      setActorsData(getChartDataByMode('actors'));
      setPublicationsData(getChartDataByMode('publications'));
    }
  }, [chartData]);

  const audienceCount = formatWithCustomGigaBillion(
    inauthenticBehavior?.total_audience || 0,
  );
  const chartBadge =
    currentMode === 'actors'
      ? `${t('Inauthentic audience:')} ${audienceCount}`
      : '';

  const breakdownsContainerProps: any = {
    title: t('Inauthentic behavior distribution'),
    onTabChange: (tabValue: 'actors' | 'publications') => {
      setCurrentMode(tabValue);
    },
    tabs: [
      { name: t('Actors'), value: 'actors' },
      { name: t('Publications'), value: 'publications' },
    ],
    flexGrow: 2,
  };

  return (
    <BreakdownsContainer {...breakdownsContainerProps}>
      <PieChart
        chartData={currentMode === 'actors' ? actorsData : publicationsData}
        isClickable={isClickable}
        onSelect={(index) => {
          if (index === null || !inauthenticBehavior) return;
          let selectedInauthenticType: string | null = null;
          if (chartData) {
            selectedInauthenticType =
              chartData[index]?.inauthentic_type !== null
                ? chartData[index]?.inauthentic_type.toLowerCase()
                : null;
          }

          if (isClickable) {
            if (selectedInauthenticType === null) {
              setTypesFilter({ value: 'is_bot', label: 'Bots', bool: true });
              setBotsExcludeFilter(true);
              setModalTitle(t('Other'));
            } else {
              setTypesFilter({ value: 'is_bot', label: 'Bots', bool: false });
              setSubtypesFilter(selectedInauthenticType);
              setModalTitle(botEntityTypes[selectedInauthenticType as keyof typeof botEntityTypes]?.title);
            }
            setCurrentWidgetName('bots_breakdown');
            openModal();
          }
        }}
        legendConfig={{
          position: 'bottom',
          textStyle: { fontSize: 14 },
        }}
        seriesName={currentMode === 'actors' ? t('Actors') : t('Publications')}
        chartBadge={chartBadge}
      />
    </BreakdownsContainer>
  );
};
